/**
 * billingConfigService.js
 * 
 * Org-level billing configuration stored in Firestore.
 * Admins configure which services are active, what code format to use,
 * and any custom billing codes. All agents in the org share the same config.
 * 
 * Collection: billingConfig/{organizationId}
 */

import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";

// ===== DEFAULT BILLING CONFIG =====
// Ships with both HCPCS and DBHDD service class codes for Georgia

const DEFAULT_SERVICES = {
  "ICM Weekly Visit": {
    enabled: true,
    hcpcsCode: "H0039",
    dbhddCode: "ICM",
    label: "Intensive Case Management",
    unitMinutes: 15,
    maxDailyUnits: 12,
    staffTitle: "ICM Staff",
  },
  "ICM Initial Assessment": {
    enabled: true,
    hcpcsCode: "H0039",
    dbhddCode: "ICM",
    label: "ICM — Initial Assessment",
    unitMinutes: 15,
    maxDailyUnits: 16,
    staffTitle: "ICM Staff",
  },
  "PSR Session": {
    enabled: true,
    hcpcsCode: "H2017",
    dbhddCode: "PSR",
    label: "Psychosocial Rehabilitation — Individual",
    unitMinutes: 15,
    maxDailyUnits: 8,
    staffTitle: "PSR Specialist",
  },
  "CST Visit": {
    enabled: true,
    hcpcsCode: "H2015",
    dbhddCode: "CST",
    label: "Community Support Team",
    unitMinutes: 15,
    maxDailyUnits: 60,
    staffTitle: "CST Staff",
  },
  "Community Support Individual": {
    enabled: true,
    hcpcsCode: "H0036",
    dbhddCode: "CSI",
    label: "Community Support — Individual",
    unitMinutes: 15,
    maxDailyUnits: 32,
    staffTitle: "Community Support Staff",
  },
  "Peer Support": {
    enabled: true,
    hcpcsCode: "H0038",
    dbhddCode: "PSI",
    label: "Peer Support — Individual",
    unitMinutes: 15,
    maxDailyUnits: 8,
    staffTitle: "Peer Support Specialist",
  },
  "Crisis Intervention": {
    enabled: true,
    hcpcsCode: "H2011",
    dbhddCode: "CIN",
    label: "Crisis Intervention",
    unitMinutes: 15,
    maxDailyUnits: 8,
    staffTitle: "Crisis Staff",
  },
  "ACT Contact": {
    enabled: true,
    hcpcsCode: "H0039",
    dbhddCode: "ACT",
    label: "Assertive Community Treatment",
    unitMinutes: 15,
    maxDailyUnits: 60,
    staffTitle: "ACT Team Member",
  },
  "Case Management": {
    enabled: false,
    hcpcsCode: "T1017",
    dbhddCode: "CMS",
    label: "Case Management",
    unitMinutes: 15,
    maxDailyUnits: 12,
    staffTitle: "Case Manager",
  },
  "Behavioral Health Assessment": {
    enabled: false,
    hcpcsCode: "H0031",
    dbhddCode: "BHA",
    label: "BH Assessment & Service Plan Development",
    unitMinutes: 15,
    maxDailyUnits: 24,
    staffTitle: "Clinician",
  },
};

const DEFAULT_CONFIG = {
  // Which code format to display on notes
  // "hcpcs" = H0039, H2017, etc. (national codes)
  // "dbhdd" = ACT, CST, PSR, etc. (DBHDD service class codes)
  // "both"  = Shows both: "H0039 (ACT)"
  codeFormat: "hcpcs",

  // Billing unit duration in minutes (almost always 15 for GA DBHDD)
  unitMinutes: 15,

  // Auto-calculate units from session duration
  autoCalculateUnits: true,

  // State (for future multi-state support)
  state: "GA",

  // Service configurations
  services: DEFAULT_SERVICES,
};

// ===== FIRESTORE OPERATIONS =====

/**
 * Get billing config for an org. Returns defaults if none exists.
 */
export async function getBillingConfig(organizationId) {
  try {
    const ref = orgDoc("billingConfig", organizationId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      // Merge with defaults to pick up any new services added in updates
      return {
        ...DEFAULT_CONFIG,
        ...data,
        services: { ...DEFAULT_SERVICES, ...(data.services || {}) },
      };
    }
    return { ...DEFAULT_CONFIG };
  } catch (e) {
    console.error("[Billing] Failed to load config:", e);
    return { ...DEFAULT_CONFIG };
  }
}

/**
 * Save billing config for an org
 */
export async function saveBillingConfig(organizationId, config) {
  try {
    const ref = orgDoc("billingConfig", organizationId);
    await setDoc(ref, {
      ...config,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    console.log("[Billing] Config saved for org:", organizationId);
    return true;
  } catch (e) {
    console.error("[Billing] Failed to save config:", e);
    return false;
  }
}

/**
 * Subscribe to billing config changes (real-time)
 */
export function onBillingConfig(organizationId, callback) {
  const ref = orgDoc("billingConfig", organizationId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      callback({
        ...DEFAULT_CONFIG,
        ...data,
        services: { ...DEFAULT_SERVICES, ...(data.services || {}) },
      });
    } else {
      callback({ ...DEFAULT_CONFIG });
    }
  }, (error) => {
    console.error("[Billing] Config listener error:", error);
    callback({ ...DEFAULT_CONFIG });
  });
}

// ===== HELPER FUNCTIONS =====

/**
 * Get the billing code string for a service type, formatted per org config
 */
export function formatBillingCode(serviceType, config) {
  const svc = config?.services?.[serviceType];
  if (!svc) return "Unknown";

  const format = config?.codeFormat || "hcpcs";

  if (format === "hcpcs") {
    return `${svc.hcpcsCode} — ${svc.label}`;
  } else if (format === "dbhdd") {
    return `${svc.dbhddCode} — ${svc.label}`;
  } else {
    // both
    return `${svc.hcpcsCode} (${svc.dbhddCode}) — ${svc.label}`;
  }
}

/**
 * Get just the short code for a service
 */
export function getShortCode(serviceType, config) {
  const svc = config?.services?.[serviceType];
  if (!svc) return "??";

  const format = config?.codeFormat || "hcpcs";

  if (format === "hcpcs") return svc.hcpcsCode;
  if (format === "dbhdd") return svc.dbhddCode;
  return `${svc.hcpcsCode}/${svc.dbhddCode}`;
}

/**
 * Get only the enabled services (for agent dropdowns)
 */
export function getEnabledServices(config) {
  if (!config?.services) return [];
  return Object.entries(config.services)
    .filter(([, svc]) => svc.enabled)
    .map(([key, svc]) => ({
      value: key,
      label: key,
      code: getShortCode(key, config),
      fullCode: formatBillingCode(key, config),
      staffTitle: svc.staffTitle,
      unitMinutes: svc.unitMinutes || 15,
      maxDailyUnits: svc.maxDailyUnits,
    }));
}

/**
 * Calculate billing units from session duration
 */
export function calculateBillingUnits(durationSeconds, serviceType, config) {
  const svc = config?.services?.[serviceType];
  const unitMin = svc?.unitMinutes || 15;
  const durationMin = Math.round(durationSeconds / 60);
  const units = Math.ceil(durationMin / unitMin);
  const maxDaily = svc?.maxDailyUnits || 99;

  return {
    units: Math.min(units, maxDaily),
    durationMinutes: durationMin,
    unitMinutes: unitMin,
    capped: units > maxDaily,
  };
}

export { DEFAULT_CONFIG, DEFAULT_SERVICES };